/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { validateModelId, getModelErrorMessage } from '@/providers/llm-provider'

/**
 * API Error types and utilities
 */

export type ErrorType = 'payment' | 'api_key' | 'rate_limit' | 'model_error' | 'generic'

export interface ParsedError {
  type: ErrorType
  title: string
  message: string
  actionUrl?: string
  actionLabel?: string
}

/**
 * Parse API error and determine type
 */
export function parseApiError(error: any, model?: string): ParsedError {
  const errorMessage = error?.message || String(error)
  const statusCode = error?.status || error?.statusCode

  // Payment/Credits errors (402)
  if (statusCode === 402 || errorMessage.includes('402') || 
      errorMessage.toLowerCase().includes('payment') ||
      errorMessage.toLowerCase().includes('credits') ||
      errorMessage.toLowerCase().includes('insufficient funds') ||
      errorMessage.toLowerCase().includes('max_tokens')) {
    
    // Extract specific details from OpenRouter error
    let details = errorMessage
    if (errorMessage.includes('You requested up to')) {
      const match = errorMessage.match(/You requested up to ([\d,]+) tokens, but can only afford ([\d,]+)/)
      if (match) {
        details = `You requested ${match[1]} tokens but only have ${match[2]} tokens available.`
      }
    }
    
    return {
      type: 'payment',
      title: 'Insufficient Credits',
      message: `${details}\n\nPlease add more credits to your OpenRouter account to continue.`,
      actionUrl: 'https://openrouter.ai/settings/credits',
      actionLabel: 'Add Credits on OpenRouter',
    }
  }

  // API Key errors (401)
  if (statusCode === 401 || errorMessage.includes('401') ||
      errorMessage.toLowerCase().includes('api key') ||
      errorMessage.toLowerCase().includes('unauthorized') ||
      errorMessage.toLowerCase().includes('authentication')) {
    return {
      type: 'api_key',
      title: 'API Key Error',
      message: 'Your API key is missing or invalid.\n\nPlease check your API key in Settings.',
      actionUrl: undefined,
      actionLabel: undefined,
    }
  }

  // Rate limit errors (429)
  if (statusCode === 429 || errorMessage.includes('429') ||
      errorMessage.toLowerCase().includes('rate limit') ||
      errorMessage.toLowerCase().includes('too many requests')) {
    return {
      type: 'rate_limit',
      title: 'Rate Limit Exceeded',
      message: 'You have exceeded the API rate limit.\n\nPlease wait a moment before trying again.',
      actionUrl: undefined,
      actionLabel: undefined,
    }
  }

  // Model not found errors (404)
  if (statusCode === 404 || errorMessage.includes('404') ||
      errorMessage.toLowerCase().includes('not_found') ||
      errorMessage.toLowerCase().includes('model') && errorMessage.toLowerCase().includes('not found')) {

    // Try to extract model ID from error message
    let modelId = model
    if (!modelId) {
      const modelMatch = errorMessage.match(/model[:\s]+["']?([a-z0-9\-/.]+)["']?/i)
      if (modelMatch) modelId = modelMatch[1]
    }

    let message = 'The requested model was not found. This could mean:\n' +
                  '• The model ID is incorrect\n' +
                  '• The model isn\'t available in your region yet\n' +
                  '• Your API key doesn\'t have access to this model\n\n'

    // Add model-specific suggestions
    if (modelId) {
      const validation = validateModelId(modelId)
      if (validation) {
        message = validation + '\n\n' + message
      }

      // Suggest stable alternatives
      if (modelId.includes('claude-sonnet-4')) {
        message += 'Try using "claude-3-5-sonnet-20241022" (Claude 3.5 Sonnet) instead - it\'s stable and proven.'
      } else if (modelId.includes('gpt-5')) {
        message += 'Try using "gpt-4o" instead - it\'s stable and widely available.'
      } else {
        message += 'Try using a stable model like "claude-3-5-sonnet-20241022" or "gpt-4o".'
      }
    }

    return {
      type: 'model_error',
      title: 'Model Not Found',
      message,
      actionUrl: undefined,
      actionLabel: undefined,
    }
  }

  // Temperature/parameter errors (400)
  if ((statusCode === 400 || errorMessage.includes('400')) &&
      errorMessage.toLowerCase().includes('temperature')) {
    return {
      type: 'model_error',
      title: 'Model Parameter Error',
      message: 'This model doesn\'t support custom temperature values.\n\n' +
               'This is common with reasoning models (GPT-5, o1, o3).\n\n' +
               'The app will now automatically handle this for you.',
      actionUrl: undefined,
      actionLabel: undefined,
    }
  }

  // Generic error
  return {
    type: 'generic',
    title: 'API Error',
    message: errorMessage,
    actionUrl: undefined,
    actionLabel: undefined,
  }
}

